import { computed } from 'vue'
import {
  ACCESS_TYPE,
  ACTOR_TYPE,
  MENTOR_STATUS,
  PROFILE_STATUS,
  PROFILE_VISIBILITY,
  ROLE,
  SUBSCRIPTION_BILLING_CYCLE,
  SUBSCRIPTION_GROUP,
  SUBSCRIPTION_NEXT_BILLING_PERIOD_STATUS,
  SUBSCRIPTION_STATUS,
  SUBSCRIPTION_STRIPE_CONNECTION_STATUS,
  SUBSCRIPTION_TIER,
  SUBSCRIPTION_TRIAL_INTERVAL,
  SUBSCRIPTION_TYPE,
  TEAM_ROLE,
} from '@/shared/data/permissionSystem.js'
import { usePage } from '@inertiajs/vue3'

export const usePermissionSystem = () => {
  const page = usePage()

  const accessControl = computed(() => {
    const { accessControl } = page.props.currentUser
    const { subscription } = accessControl
    const { privileges } = subscription
    return {
      accessType: accessControl.accessType,
      actorType: accessControl.actorType,
      profileStatus: accessControl.profileStatus,
      profileVisibility: accessControl.profileVisibility,
      role: accessControl.role,
      teamRole: accessControl.teamRole,
      mentorStatus: accessControl.mentorStatus,
      subscription: {
        billingCycle: subscription.billingCycle,
        name: subscription.name,
        nextBillingPeriodStatus: subscription.nextBillingPeriodStatus,
        status: subscription.status,
        stripeConnection: subscription.stripeConnection,
        tier: subscription.tier,
        trialInterval: subscription.trialInterval,
        type: subscription.type,
        group: subscription.group,
        privileges: {
          connectionRequests: privileges.connectionRequests,
          doubleUpQueue: privileges.doubleUpQueue,
          filterCommunityMembers: privileges.filterCommunityMembers,
          opportunitiesPage: privileges.opportunitiesPage,
          freeCallsPerMonth: {
            limitHit: privileges.freeCallsPerMonth.limitHit,
            count: privileges.freeCallsPerMonth.count,
            currentCount: privileges.freeCallsPerMonth.currentCount,
            renewalLimitDate: privileges.renewalLimitDate,
          },
          freeCalls: {
            limitHit: privileges.freeCalls.limitHit,
            count: privileges.freeCalls.count,
            currentCount: privileges.freeCalls.currentCount,
          },
          // Feature description: Tit for tat, 1 call done as mentor = 1 available free call as mentee
          freePaybackCalls: {
            limitHit: privileges.freePaybackCalls.limitHit,
            count: privileges.freePaybackCalls.count,
            currentCount: privileges.freePaybackCalls.currentCount,
          },
          favoritedReviews: {
            limitHit: privileges.favoritedReviews.limitHit,
            count: privileges.favoritedReviews.count,
            currentCount: privileges.favoritedReviews.currentCount,
          },
        },
      },
    }
  })

  const isUserLoggedIn = computed(
    () => page.props.currentUser.accessControl.role !== ROLE.GUEST,
  )

  const hasPaidTeam = computed(() => {
    return (
      isSubscriptionStatus(SUBSCRIPTION_STATUS.PAID) &&
      isSubscriptionTier(SUBSCRIPTION_TIER.TEAM)
    )
  })

  const checkAccess = (object, userValue, value) => {
    const validValues = Object.values(object)
    if (!validValues.includes(value)) {
      throw new Error(
        `[Permission System - Access] Invalid value. Valid values types are: ${validValues.join(
          ', ',
        )}`,
      )
    }

    return accessControl.value[userValue].toLowerCase() === value.toLowerCase()
  }

  const checkSubscriptionAccess = (object, subscriptionValue, value) => {
    const validValues = Object.values(object)
    if (!validValues.includes(value)) {
      throw new Error(
        `[Permission System - Subscription] Invalid value. Valid values types are: ${validValues.join(
          ', ',
        )}`,
      )
    }

    return (
      accessControl.value.subscription[subscriptionValue].toLowerCase() ===
      value.toLowerCase()
    )
  }

  const hasAccess = (accessType) => {
    return checkAccess(ACCESS_TYPE, 'accessType', accessType)
  }

  const isActorType = (actorType) => {
    return checkAccess(ACTOR_TYPE, 'actorType', actorType)
  }

  const isMentorStatus = (mentorStatus) => {
    return checkAccess(MENTOR_STATUS, 'mentorStatus', mentorStatus)
  }

  const isProfileStatus = (profileStatus) => {
    return checkAccess(PROFILE_STATUS, 'profileStatus', profileStatus)
  }

  const isProfileVisibility = (profileVisibility) => {
    return checkAccess(
      PROFILE_VISIBILITY,
      'profileVisibility',
      profileVisibility,
    )
  }

  const hasRole = (role) => {
    return checkAccess(ROLE, 'role', role)
  }

  const hasTeamRole = (teamRole) => {
    return checkAccess(TEAM_ROLE, 'teamRole', teamRole)
  }

  const isSubscriptionBillingCycle = (billingCycle) => {
    return checkSubscriptionAccess(
      SUBSCRIPTION_BILLING_CYCLE,
      'billingCycle',
      billingCycle,
    )
  }

  const isSubscriptionNextBillingPeriodStatus = (nextBillingPeriodStatus) => {
    return checkSubscriptionAccess(
      SUBSCRIPTION_NEXT_BILLING_PERIOD_STATUS,
      'nextBillingPeriodStatus',
      nextBillingPeriodStatus,
    )
  }

  const isSubscriptionStatus = (status) => {
    return checkSubscriptionAccess(SUBSCRIPTION_STATUS, 'status', status)
  }

  const isSubscriptionStripeConnectionStatus = (stripeConnection) => {
    return checkSubscriptionAccess(
      SUBSCRIPTION_STRIPE_CONNECTION_STATUS,
      'stripeConnection',
      stripeConnection,
    )
  }

  const isSubscriptionTier = (tier) => {
    return checkSubscriptionAccess(SUBSCRIPTION_TIER, 'tier', tier)
  }

  const isSubscriptionType = (type) => {
    return checkSubscriptionAccess(SUBSCRIPTION_TYPE, 'type', type)
  }

  const isSubscriptionGroup = (group) => {
    return checkSubscriptionAccess(SUBSCRIPTION_GROUP, 'group', group)
  }

  const isSubscriptionTrialInterval = (trialInterval) => {
    return checkSubscriptionAccess(
      SUBSCRIPTION_TRIAL_INTERVAL,
      'trialInterval',
      trialInterval,
    )
  }

  const hasObjectPermissions = (object, lookingForPermissions) => {
    if (!Object.keys(object).includes('permissions')) {
      throw new Error(
        `[Permission System] Invalid object. Object must have a permissions property.`,
      )
    }
    return object.permissions.some(
      (permission) =>
        permission.toLowerCase() === lookingForPermissions.toLowerCase(),
    )
  }

  return {
    accessControl,
    isUserLoggedIn,
    hasAccess,
    hasPaidTeam,
    hasObjectPermissions,
    isActorType,
    isProfileStatus,
    isProfileVisibility,
    hasRole,
    hasTeamRole,
    isSubscriptionBillingCycle,
    isSubscriptionNextBillingPeriodStatus,
    isSubscriptionStatus,
    isSubscriptionStripeConnectionStatus,
    isSubscriptionTier,
    isSubscriptionType,
    isSubscriptionGroup,
    isSubscriptionTrialInterval,
    isMentorStatus,
  }
}
